import { LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { PromotionService } from '~/service/service.promotion';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import {
	ActionsColumn,
	AvatarColumn,
	DateTimeColumn,
	ImageColumn,
	LinkColumn,
	useGridColumns,
} from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';
import { Box, Row } from '~/view/view.box';

export const PromotionListPage = observer(() => {
	const [promotions, ui, auth] = useService(PromotionService, UIService, AuthService);
	const columns = useGridColumns<API.PromotionItem>(() => [
		{ field: 'id', headerName: 'ID' },
		AvatarColumn({ field: 'logo', headerName: 'Logo', sortable: false }),
		ImageColumn({ field: 'bannerImage', headerName: 'Banner Image', width: 150, height: 40 }),
		{
			field: 'brandColors',
			headerName: 'Brand colors',
			sortable: false,
			minWidth: 150,
			renderCell: params => (
				<Row gap={2} alignItems="center">
					{params.value?.map((c: string) => <Box key={c} width={24} height={24} bgcolor={c} />)}
				</Row>
			),
		},
		{ field: 'offersCount', headerName: 'Offers', width: 100, sortable: false },
		{ field: 'bestsellersCount', headerName: 'Bestsellers', width: 100, sortable: false },
		{ field: 'newbiesCount', headerName: 'Newbies', width: 100, sortable: false },
		LinkColumn({
			flex: 1,
			sortable: false,
			field: 'brandName',
			headerName: 'Brand name',
			getLink: row => AppRoutes.BrandEdit.buildPath({ id: row.brandId }),
		}),
		DateTimeColumn({ field: 'activeFrom', headerName: 'Active From' }),
		DateTimeColumn({ field: 'activeTo', headerName: 'Active To', width: 150 }),
		DateTimeColumn({ field: 'createdAt', headerName: 'Created At' }),
		DateTimeColumn({ field: 'updatedAt', headerName: 'Updated At' }),
		ActionsColumn<API.PromotionItem>({
			editRoute: AppRoutes.PromotionEdit.buildPath,
			onDelete: promotions.delete,
			permission: 'promotion',
		}),
	]);

	ui.usePageTitle('Promotions');
	useEffect(() => promotions.loacdAndSubscribe(), [promotions]);

	return (
		<GridContainer async={promotions.async}>
			<DataGrid
				initialState={promotions.gridState.initial()}
				loading={promotions.async.loading}
				rowCount={promotions.totalCount}
				rows={promotions.items.slice()}
				columns={columns}
				disableColumnFilter
				paginationMode="server"
				sortingMode="server"
				onStateChange={promotions.gridState.onStateChange}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No promotions found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar: () => (
						<GridToolbarView
							onReload={promotions.reload}
							gridState={promotions.gridState}
							onCreateUrl={
								auth.can('promotion', 'CREATE') ? AppRoutes.PromotionNew.buildPath({}) : undefined
							}
							exportEnabled={auth.can('promotion', 'EXPORT')}
						/>
					),
				}}
			/>
		</GridContainer>
	);
});
